import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button } from '@mui/material'
import { useReadUserProfile } from '@/api/user-manager'
import {
  clearConfirmedSteps,
  useConfirmedSteps,
} from '@/pages/credit/journeys/confirmed-steps'
import {
  isProposalExpired,
  useReadApplication,
} from '@/api/financial/application/read-application'
import {
  useColleges,
  useSetApplicationCollege,
} from '@/api/financial/application/college'
import {
  useAllowanceLimits,
  useAllowancePreview,
  useSaveAllowanceSimulation,
} from '@/api/financial/simulation/simulate-allowance'
import {
  useCompleteProponents,
  useUpdatePerson,
} from '@/api/financial/person/update-person'
import { useMunicipalities } from '@/api/financial/person/municipalities'
import {
  useDeleteDocument,
  useUploadDocument,
} from '@/api/financial/file/documents'
import {
  useAcceptProposal,
  usePreAnalysis,
  useReissueProposal,
  useSendCommittee,
} from '@/api/financial/committee/committee'
import { JourneySheet } from '@/components/credit-journey/journey-sheet'
import { JourneyShell } from '@/components/credit-journey/journey-shell'
import { formatTaxId } from '@/helpers'
import { saveProfile } from '@/pages/session/profile/save-profile'
import { estimatedSemester } from '@/pages/session/profile/profile-steps'
import { creditProducts } from '@/pages/credit/products/credit-products'
import {
  hydrationStamp,
  shouldRehydrate,
} from '@/pages/credit/journeys/rehydrate'
import { routes } from '@/routes'
import { CepResponse } from '@/types'
import {
  AllowanceStep,
  allowanceSteps,
  isStepComplete,
  requiredDocuments,
  resolveStep,
  stageOf,
  stepProgress,
  visibleSteps,
} from './allowance-flow'
import {
  clearDraft,
  clearDraftFields,
  mergeDraft,
  readDraft,
  stepFields,
  writeDraft,
} from './allowance-draft'
import {
  AllowanceForm,
  emptyForm,
  formFromApplication,
  isStepValid,
  personPayloadForStep,
} from './allowance-form'
import { AllowanceScreens } from './allowance-screens'
import { JourneySkeleton } from '@/components/credit-journey/journey-skeleton'

const titles: Record<AllowanceStep, string> = {
  fac: 'Sobre sua faculdade',
  valor: 'Quanto você quer receber por mês?',
  plano: '',
  eu: 'Seus dados',
  end: 'Onde você mora?',
  civil: 'Estado civil e declarações',
  pais: 'Dados dos seus pais',
  uso: 'Como você pretende usar a Mesada?',
  renda: 'Renda da sua família',
  msg: 'Quer contar mais sobre você?',
  traj: 'Sua trajetória',
  planos: 'Seus planos após a formatura',
  conta: 'Sua conta para receber a Mesada',
  docs: 'Envie os documentos',
  analise: '',
  proposta: 'Sua Mesada foi aprovada!',
  assinar: 'Assine o contrato',
  fim: '',
}

const hints: Partial<Record<AllowanceStep, string>> = {
  valor: 'Você recebe até a formatura.',
  plano: 'Confira a simulação e clique no plano selecionado.',
  uso: 'Escolha quantas quiser.',
  conta:
    'Informe a conta para o depósito mensal. Ela precisa estar no seu CPF.',
  proposta: 'Confira a proposta antes de aceitar.',
}

const nextLabels: Partial<Record<AllowanceStep, string>> = {
  conta: 'Salvar e ir para documentos',
  docs: 'Enviar para análise',
  proposta: 'Aceitar proposta',
  analise: 'Ir para o Resumo',
  fim: 'Ir para o Resumo',
}

function semesterOf(graduation: string, fallback: number | null) {
  if (/^\d{4}-\d{2}$/.test(graduation)) {
    const [year, month] = graduation.split('-').map(Number)
    return estimatedSemester(year, month - 1)
  }
  return fallback ?? 0
}

export function AllowanceJourneyPage() {
  const { application_key: applicationKey = '', step: stepParam } = useParams()
  const navigate = useNavigate()
  const application = useReadApplication(applicationKey)
  const user = useReadUserProfile()
  const [form, setForm] = useState<AllowanceForm>(emptyForm)
  const [hydratedStamp, setHydratedStamp] = useState<string | null>(null)
  const [collegeSearch, setCollegeSearch] = useState('')
  const [addressState, setAddressState] = useState('')
  const [ibge, setIbge] = useState('')
  const [exitOpen, setExitOpen] = useState(false)
  const [saving, setSaving] = useState<'saving' | 'saved' | 'error'>('saved')
  const { confirmed, confirm } = useConfirmedSteps(applicationKey)

  const colleges = useColleges(collegeSearch)
  const municipalities = useMunicipalities(addressState)
  const data = application.data
  const person = data?.application_persons[0]?.person
  const stamp = hydrationStamp(applicationKey, person?.key)
  const hydrated = hydratedStamp?.startsWith(`${applicationKey}:`) ?? false

  useEffect(() => {
    if (!data || user.isLoading || !shouldRehydrate(hydratedStamp, stamp))
      return
    const draft = readDraft(applicationKey)
    setForm(
      mergeDraft(
        formFromApplication(data, user.data?.profile?.graduation_date),
        draft,
      ),
    )
    const stateCode =
      draft.address_state ||
      data.application_persons[0]?.person.address[0]?.municipality?.state
    if (stateCode) setAddressState(stateCode)
    setHydratedStamp(stamp)
  }, [
    applicationKey,
    data,
    hydratedStamp,
    stamp,
    user.isLoading,
    user.data?.profile?.graduation_date,
  ])

  useEffect(() => {
    const match = municipalities.data?.find((item) => item.ibge_code === ibge)
    if (!match) return
    const municipalityId = String(match.id)
    setForm((current) => ({ ...current, municipality_id: municipalityId }))
    writeDraft(applicationKey, { municipality_id: municipalityId })
  }, [applicationKey, municipalities.data, ibge])

  const facts = useMemo(() => {
    const documents = person?.documents?.map((item) => item.type) ?? []
    const completed = Object.fromEntries(
      allowanceSteps.map((item) => [
        item,
        isStepComplete(item, person, {
          college: Boolean(data?.college),
          graduation: Boolean(user.data?.profile?.graduation_date),
          hasSimulation: Boolean(data?.simulation),
          documents,
        }) &&
          // dados já cadastrados só contam depois de conferidos nesta solicitação
          (stageOf(item) !== 1 || confirmed.includes(item)),
      ]),
    )
    return {
      collegePrivate:
        data?.college?.college_type === 'private' || form.collegePrivate,
      hasSimulation: Boolean(data?.simulation),
      status: data?.status ?? 'incomplete',
      serverStep: data?.step ?? 'initial_proponents',
      completed,
    }
  }, [
    data,
    person,
    user.data?.profile?.graduation_date,
    form.collegePrivate,
    confirmed,
  ])

  const step = data ? resolveStep(stepParam, facts) : 'fac'
  const steps = visibleSteps()

  useEffect(() => {
    if (!data || stepParam === step) return
    navigate(`/credit/applications/${applicationKey}/${step}`, {
      replace: true,
    })
  }, [data, step, stepParam, applicationKey, navigate])

  const graduationDate = form.graduation ? `${form.graduation}-01` : ''
  const onAmountStep = step === 'valor' || step === 'plano'
  const allowanceLimits = useAllowanceLimits(
    onAmountStep && graduationDate ? graduationDate : null,
  )
  const limits = allowanceLimits.data
  const amountInRange = Boolean(
    limits && form.amount >= limits.min && form.amount <= limits.max,
  )
  const preview = useAllowancePreview(
    step === 'plano' && amountInRange
      ? {
          amount: form.amount,
          graduation_date: graduationDate,
          grace: form.grace,
          is_combo: form.combo,
        }
      : null,
  )

  const setCollege = useSetApplicationCollege(applicationKey)
  const saveSimulation = useSaveAllowanceSimulation(applicationKey)
  const preAnalysis = usePreAnalysis(applicationKey)
  const updatePerson = useUpdatePerson(applicationKey, person?.key ?? '')
  const complete = useCompleteProponents(applicationKey)
  const upload = useUploadDocument(applicationKey)
  const removeDocument = useDeleteDocument(applicationKey)
  const send = useSendCommittee(applicationKey)
  const accept = useAcceptProposal(applicationKey)
  const reissue = useReissueProposal(applicationKey)
  const proposal = data?.committee?.proposal ?? null
  const proposalExpired = proposal ? isProposalExpired(proposal) : false

  const patch = useCallback(
    (partial: Partial<AllowanceForm>) => {
      setForm((current) => ({ ...current, ...partial }))
      writeDraft(applicationKey, partial)
    },
    [applicationKey],
  )

  useEffect(() => {
    if (!limits || !onAmountStep) return
    if (form.amount >= limits.min && form.amount <= limits.max) return
    patch({
      amount: Math.min(limits.max, Math.max(limits.min, form.amount)),
    })
  }, [limits, form.amount, onAmountStep, patch])

  function go(nextStep: AllowanceStep) {
    navigate(`/credit/applications/${applicationKey}/${nextStep}`)
  }

  function back() {
    const index = steps.indexOf(step)
    if (index > 0) go(steps[index - 1])
  }

  const semester = semesterOf(
    form.graduation,
    user.data?.profile?.current_semester ?? null,
  )
  const minSemester = creditProducts.allowance.minSemester ?? 9
  const tuition = Number(form.tuition)
  const attached = new Set(person?.documents?.map((item) => item.type) ?? [])
  const phoneVerified = Boolean(
    user.data?.alias?.find((alias) => alias.type === 'phone' && alias.verified),
  )
  const nextDisabled = !isStepValid(step, form, {
    semester,
    minSemester,
    limits: limits ?? null,
    hasPreview: Boolean(preview.data),
    hasRequiredDocs: requiredDocuments.every((type) => attached.has(type)),
    hasProposal: Boolean(proposal && !proposal.accepted_at && !proposalExpired),
    denied: data?.status === 'denied',
    hasVerifiedPhone: phoneVerified,
  })

  async function next() {
    if (step === 'analise' || step === 'fim') {
      clearDraft(applicationKey)
      navigate(routes.credit.index)
      return
    }
    const following = steps[steps.indexOf(step) + 1]
    const personPayload = person ? personPayloadForStep(step, form) : null
    let persisted = false
    try {
      setSaving('saving')
      if (step === 'fac') {
        await setCollege.mutateAsync(form.collegeKey)
        await saveProfile({ graduation_date: graduationDate })
        await application.refetch()
        persisted = true
        go('valor')
      } else if (step === 'plano') {
        await saveSimulation.mutateAsync({
          amount: form.amount,
          graduation_date: graduationDate,
          grace: form.grace,
          is_combo: form.combo,
          monthly_tuition: form.collegePrivate ? tuition : undefined,
        })
        await preAnalysis.mutateAsync()
        const refreshed = await application.refetch()
        persisted = true
        go(refreshed.data?.status === 'denied' ? 'analise' : 'eu')
      } else if (personPayload) {
        await updatePerson.mutateAsync(personPayload)
        await application.refetch()
        persisted = true
        confirm(step)
        if (following) go(following)
      } else if (person && step === 'conta') {
        await updatePerson.mutateAsync({
          bank_account: {
            bank_code: form.bank_code,
            agency_number: form.agency_number,
            agency_digit: form.agency_digit,
            account_number: form.account_number,
            account_digit: form.account_digit,
            account_type: form.account_type,
          },
        })
        try {
          await complete.mutateAsync()
        } catch (error) {
          const details = (error as { details?: { step?: AllowanceStep } })
            .details
          await application.refetch()
          if (details?.step) {
            go(details.step)
            setSaving('saved')
            return
          }
          throw error
        }
        await application.refetch()
        persisted = true
        go('docs')
      } else if (step === 'docs') {
        await send.mutateAsync()
        await application.refetch()
        persisted = true
        go('analise')
      } else if (step === 'proposta' && proposal) {
        await accept.mutateAsync(proposal.key)
        await application.refetch()
        go('assinar')
      } else if (following) {
        go(following)
      }
      if (persisted) {
        if (step === 'docs') {
          clearDraft(applicationKey)
          clearConfirmedSteps(applicationKey)
        } else {
          const fields = stepFields[step]
          if (fields) clearDraftFields(applicationKey, fields)
        }
      }
      setSaving('saved')
    } catch {
      setSaving('error')
    }
  }

  if (!applicationKey) return <Navigate to={routes.credit.index} replace />
  if (application.isLoading || !data || !hydrated) {
    return <JourneySkeleton />
  }
  if (data.product !== 'allowance') {
    return <Navigate to={routes.credit.index} replace />
  }

  const name =
    [user.data?.first_name, user.data?.last_name].filter(Boolean).join(' ') ||
    person?.name ||
    ''
  const emails =
    user.data?.alias?.filter((alias) => alias.type === 'email') ?? []
  const email =
    (emails.find((alias) => alias.verified) ?? emails[0])?.data ?? ''
  const phone =
    user.data?.alias?.find((alias) => alias.type === 'phone')?.data ?? ''
  const progress = stepProgress(step)
  const nextLabel = nextLabels[step] ?? 'Continuar'
  const privateCollege = facts.collegePrivate
  const title =
    step === 'renda' && privateCollege
      ? 'Bolsa, financiamento e renda'
      : titles[step]
  const hint =
    step === 'renda' && privateCollege
      ? 'Se não tiver bolsa ou financiamento, é só informar a renda.'
      : hints[step]
  // depois do envio não há mais nada a preencher: sair não pede confirmação
  const filling = stageOf(step) < 3

  return (
    <>
      <JourneyShell
        stage={stageOf(step)}
        progress={progress}
        title={title}
        hint={hint}
        done={step === 'fim'}
        onBack={
          steps.indexOf(step) > 0 && step !== 'analise' && step !== 'fim'
            ? back
            : undefined
        }
        onExit={() =>
          filling ? setExitOpen(true) : navigate(routes.credit.index)
        }
        onNext={next}
        nextLabel={nextLabel}
        nextDisabled={nextDisabled}
        saving={saving}
        showSaved={stageOf(step) < 2}
      >
        <AllowanceScreens
          step={step}
          form={form}
          patch={patch}
          colleges={colleges.data ?? []}
          onCollegeSearch={setCollegeSearch}
          semester={semester}
          minSemester={minSemester}
          limits={limits ?? null}
          preview={preview.data}
          previewLoading={preview.isFetching}
          identity={{
            name,
            taxId: formatTaxId(user.data?.tax_id ?? ''),
            email,
            phone,
          }}
          municipalities={municipalities.data ?? []}
          addressState={addressState}
          onStateChange={(stateCode) => {
            setAddressState(stateCode)
            setIbge('')
            patch({ municipality_id: '' })
            writeDraft(applicationKey, { address_state: stateCode })
          }}
          onCep={(cep: CepResponse) => {
            setAddressState(cep.state_code)
            writeDraft(applicationKey, { address_state: cep.state_code })
            setIbge(cep.ibge_code)
            patch({
              zip_code: cep.zipcode,
              street: cep.street,
              district: cep.district,
            })
          }}
          documents={person?.documents ?? []}
          onUpload={(type, file) => {
            if (!person) return
            upload.mutate({ type, file, personKey: person.key })
          }}
          onRemove={(documentKey) => removeDocument.mutateAsync(documentKey)}
          uploadingType={upload.isPending ? upload.variables?.type : undefined}
          phoneVerified={phoneVerified}
          denied={data.status === 'denied'}
          proposal={proposal}
          proposalExpired={proposalExpired}
          reissuing={reissue.isPending}
          onReissue={async () => {
            if (!proposal) return
            await reissue.mutateAsync(proposal.key)
            await application.refetch()
            go('analise')
          }}
        />
      </JourneyShell>

      <JourneySheet
        open={exitOpen}
        title="Sair da solicitação?"
        description="Tudo o que você preencheu fica salvo. Para continuar, abra Solicitações."
        onClose={() => setExitOpen(false)}
      >
        <Button
          variant="contained"
          onClick={() => navigate(routes.credit.applications)}
        >
          Sair e continuar depois
        </Button>
        <Button variant="text" onClick={() => setExitOpen(false)}>
          Continuar preenchendo
        </Button>
      </JourneySheet>
    </>
  )
}
